<template>
  <guest-layout style="max-width: 1000px">
    <q-toolbar v-if="accounts === undefined || actionAccounts.length > 0">
      <q-toolbar-title class="text-center">
        Please Import Your Social Posts
      </q-toolbar-title>
    </q-toolbar>
    <access-token-login
      :access-token="accessToken"
      @logged-in="loggedIn = true"
      @expired="isExpired = true"
    />
    <message v-if="isExpired">
      This link has expired. Please check your email inbox for a more recent link.
    </message>
    <div v-else-if="loggedIn" class="bg-grey-2 q-pa-md rounded-borders">
      <accounts-list
        v-if="accounts === undefined || actionAccounts.length"
        :client-id="clientId"
        v-model:platforms="actionPlatforms"
        :accounts="accounts"
        v-model:platform="platform"
        @update:accounts="setAccounts"
      >
        <template v-slot="{ account }">
          <client-actions
            v-if="account && !finishedIds.includes(account.id)"
            :account="account"
          />
          <message v-else-if="account && finishedIds.includes(account.id)" class="q-ma-md">
            Your {{ account.socialSite.name }} posts are importing.
            No further action is needed for {{ account.socialSite.name }}.
          </message>
        </template>
      </accounts-list>
      <message v-else class="text-center">
        Currently, you do not need to import any posts.<br>No further action is required by you.
      </message>
    </div>
  </guest-layout>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import AccessTokenLogin from '@/iam/authentication/access-token/AccessTokenLogin.vue';
import Message from '@/components/message/Message.vue';
import GuestLayout from '@/layout/GuestLayout.vue';
import { Account, Platform } from '@/social/types';
import { requiresReconnect } from '@/social/account';
import eventBus from '@/messaging/event-bus';
import { AccountReconnected } from '@/social/account/action/reconnect/index';
import AccountsList from '@/social/account/list/AccountsList.vue';
import ClientActions from '@/social/account/action/ClientActions.vue';
import { PostImportCreated } from '@/social/post/import';

interface Data {
  loggedIn: boolean,
  isExpired: boolean,
  platforms: Platform[],
  accounts?: Account[],
  platform: Platform|null,
  finishedIds: number[],
}

export default defineComponent({
  components: {
    ClientActions,
    AccountsList,
    GuestLayout,
    Message,
    AccessTokenLogin,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    accessToken: {
      type: String,
      required: true,
    },
  },
  data(): Data {
    return {
      loggedIn: false,
      isExpired: false,
      platforms: [],
      accounts: undefined,
      platform: null,
      finishedIds: [],
    };
  },
  computed: {
    actionAccounts(): Account[] {
      if (!this.accounts) return [];
      return this.accounts.filter(account => (
        requiresReconnect(account)
        || this.finishedIds.includes(account.id)
      ));
    },
    actionPlatforms: {
      get(): Platform[] {
        if (!this.actionAccounts) return [];
        return this.platforms.filter(
          p => this.actionAccounts.find(a => a.socialSite.id === p.id) !== undefined,
        );
      },
      set(platforms: Platform[]): void {
        this.platforms = platforms;
      },
    },
  },
  methods: {
    setAccounts(accounts: Account[]): void {
      this.accounts = accounts;
      const requiringAction = accounts.filter(requiresReconnect);
      if (requiringAction.length > 0 && !this.platform) {
        this.platform = requiringAction[0].socialSite;
      }
    },
    onReconnected(event: AccountReconnected) {
      this.finishedIds.push(event.account.id);
    },
    onImportCreated(event: PostImportCreated): void {
      this.finishedIds.push(event.postImport.accountId);
    },
  },
  created() {
    eventBus.$on(AccountReconnected.eventName, this.onReconnected);
    eventBus.$on(PostImportCreated.eventName, this.onImportCreated);
  },
  beforeUnmount() {
    eventBus.$off(AccountReconnected.eventName, this.onReconnected);
    eventBus.$off(PostImportCreated.eventName, this.onImportCreated);
  },
  meta: {
    title: 'Import Posts',
  },
});
</script>
